import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getLiveClasses, subscribeToLiveClasses } from '../../firebase/collections';
import { 
  Video, 
  Calendar, 
  Clock, 
  Users, 
  Search,
  Filter,
  Play,
  ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const LiveClasses = () => {
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterExam, setFilterExam] = useState('');

  useEffect(() => {
    if (!currentUser) return;

    const loadLiveClasses = async () => {
      try {
        setLoading(true);
        const result = await getLiveClasses();
        if (result.success) {
          setLiveClasses(result.data || []);
          if (result.indexError) {
            console.warn('Firestore index may be missing. Live classes loaded but may not be sorted correctly.');
          }
        } else {
          console.error('Failed to load live classes:', result.error);
          toast.error('Failed to load live classes. Please try again.');
          setLiveClasses([]);
        }
      } catch (error) {
        console.error('Error loading live classes:', error);
        toast.error('Failed to load live classes. Please refresh the page.');
        setLiveClasses([]);
      } finally {
        setLoading(false);
      }
    };

    loadLiveClasses();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToLiveClasses((result) => {
      if (result.success) {
        setLiveClasses(result.data);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  const filteredClasses = liveClasses.filter(liveClass => {
    const matchesSearch = liveClass.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         liveClass.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         liveClass.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || liveClass.status === filterStatus;
    const matchesExam = !filterExam || liveClass.exam === filterExam;
    
    return matchesSearch && matchesStatus && matchesExam;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'live': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canJoinClass = (liveClass) => {
    const now = new Date();
    const scheduledAt = liveClass.scheduledAt ? new Date(liveClass.scheduledAt) : null;
    const duration = liveClass.duration || 60;
    const endTime = scheduledAt ? new Date(scheduledAt.getTime() + duration * 60000) : null;

    if (liveClass.status === 'live') return true;
    if (liveClass.status === 'scheduled' && scheduledAt) {
      // Allow joining 5 minutes before scheduled time
      const joinTime = new Date(scheduledAt.getTime() - 5 * 60000);
      return now >= joinTime && now <= endTime;
    }
    return false;
  };

  const handleJoinClass = (liveClass) => {
    // If meetingType is 'internal' or no meetingLink, use built-in interface
    if (liveClass.meetingType === 'internal' || !liveClass.meetingLink) {
      navigate(`/live-class/${liveClass.id}`);
    } else if (liveClass.meetingLink) {
      // Open external meeting link (Zoom/Meet) in new tab
      window.open(liveClass.meetingLink, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Meeting link not available for this class');
    }
  };

  const exams = ['CAT', 'JEE', 'NEET', 'GATE', 'UPSC', 'Banking'];
  const statuses = ['scheduled', 'live', 'completed', 'cancelled'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Live Classes
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Join live interactive classes with your instructors
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center dark:text-white">
          <Filter size={16} className="mr-2" /> Filters
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Search classes..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Exam</label>
            <select
              value={filterExam}
              onChange={(e) => setFilterExam(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Exams</option>
              {exams.map(exam => (
                <option key={exam} value={exam}>{exam}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterExam('');
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Live Classes List */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <Video className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Loading Live Classes...
          </h3>
        </motion.div>
      ) : filteredClasses.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <Video className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {liveClasses.length === 0 ? 'No Live Classes Available' : 'No Classes Match Filters'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {liveClasses.length === 0 
              ? 'Check back later for upcoming live classes.'
              : 'Try adjusting your search criteria or filters.'
            }
          </p>
        </motion.div>
      ) : (
        <div className="grid gap-6">
          {filteredClasses.map((liveClass, index) => (
            <motion.div
              key={liveClass.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {liveClass.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {liveClass.subject} • {liveClass.exam} • {liveClass.instructorName}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded ${getStatusColor(liveClass.status)}`}>
                    {liveClass.status.charAt(0).toUpperCase() + liveClass.status.slice(1)}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDateTime(liveClass.scheduledAt)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {liveClass.duration || 60} minutes
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {liveClass.participantCount || 0}/{liveClass.maxParticipants || 50} participants
                  </span>
                </div>
              </div>

              {liveClass.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  {liveClass.description}
                </p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {liveClass.meetingType === 'internal' && (
                    <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs px-2 py-1 rounded">
                      Built-in Interface
                    </span>
                  )}
                  {liveClass.meetingType === 'external' && (
                    <span className="bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 text-xs px-2 py-1 rounded">
                      External (Zoom/Meet)
                    </span>
                  )}
                  {liveClass.price > 0 && (
                    <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs px-2 py-1 rounded">
                      ₹{liveClass.price}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleJoinClass(liveClass)}
                  disabled={!canJoinClass(liveClass) && liveClass.status !== 'live'}
                  className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                    (canJoinClass(liveClass) || liveClass.status === 'live')
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {liveClass.status === 'live' || canJoinClass(liveClass) ? (
                    <>
                      <Play size={16} />
                      <span>
                        {liveClass.meetingType === 'internal' ? 'Join Class' : 'Join Meeting'}
                      </span>
                    </>
                  ) : (
                    <>
                      <Calendar size={16} />
                      <span>Scheduled</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveClasses;

